import { Search, X, RotateCcw } from 'lucide-react';
import { getAslPathToRoot } from '../../../lib/asl-tree.js';

function AslTreeList({ nodes = [], activeKey, activePathKeys, onSelect }) {
  return nodes.map((node) => {
    const isActive = activeKey === node.key;
    const isInActivePath = activePathKeys.has(node.key);
    return (
      <div key={node.key}>
        <button type="button" data-asl-key={node.key} data-asl-level={node.level}
          aria-current={isActive ? 'page' : undefined}
          data-asl-active-path={isInActivePath ? 'true' : undefined}
          onClick={() => onSelect?.(node.key)} style={{ paddingLeft: `${Math.max(0, node.level - 3) * 0.75}rem` }}
          className={`flex w-full rounded-lg px-2.5 py-1.5 text-left text-xs ${isActive ? 'font-bold text-[var(--primary)]' : isInActivePath ? 'font-semibold text-[var(--primary)]' : 'text-[var(--secondary)] hover:bg-[var(--surface-container-low,#f8fafc)]'}`}>
          {node.name}
        </button>
        {node.children?.length > 0 && (
          <div data-asl-sub-group="true" className="ml-2.5 mt-0.5 mb-1 border-l-2 border-[var(--border)] pl-2">
            <AslTreeList nodes={node.children} activeKey={activeKey} activePathKeys={activePathKeys} onSelect={onSelect} />
          </div>
        )}
      </div>
    );
  });
}

export function SidebarFilterLayout({
  categories = [],
  aslTree = [],
  activeAslCategory = '',
  onSelectAslCategory,
  activeCategory = '',
  activeSubcategory = '',
  onSelectSubcategory,
  search = '',
  onSearchChange,
  onSearchSubmit,
  manufacturers = [],
  selectedManufacturer = '',
  onSelectManufacturer,
  onResetFilters,
}) {
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (onSearchSubmit) onSearchSubmit(e);
  };

  const hasActiveFilters = Boolean(
    activeCategory || activeSubcategory || selectedManufacturer || (search && search.trim())
  );

  // Active category drives the drill-down block (subcategories with live counts)
  const activeCategoryObj = (categories || []).find((cat) => cat.slug === activeCategory);
  const activeSubcategories = (activeCategoryObj?.subcategories || []).filter(
    (sub) => (sub.count ?? 0) > 0 || activeSubcategory === sub.slug
  );

  const activeAslPath = getAslPathToRoot(aslTree, activeAslCategory);
  const activeAslNode = activeAslPath[activeAslPath.length - 1] || null;
  const activeLevelTwo = activeAslPath.find((node) => node.level === 2);
  const activePathKeys = new Set(activeAslPath.map((node) => node.key));
  const visibleAslTree = activeAslNode?.level === 1
    ? (activeAslNode.children || [])
    : activeLevelTwo
      ? (activeLevelTwo.children || [])
      : aslTree;

  return (
    <aside data-filter-variant="sidebar" className="space-y-6">

      {/* Search Filter Box */}
      <form onSubmit={handleFormSubmit} className="relative">
        <input
          type="search"
          placeholder="Suchbegriff oder SKU..."
          value={search}
          onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
          className="w-full rounded-xl border border-[var(--border)] bg-white pl-9 pr-8 py-2.5 text-xs text-[var(--foreground)] placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[var(--ring)] focus:border-[var(--primary)] transition-all shadow-2xs"
        />
        <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground pointer-events-none" />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange && onSearchChange('')}
            className="absolute right-2.5 top-3 text-muted-foreground hover:text-[var(--foreground)] cursor-pointer"
            aria-label="Suchtext löschen"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </form>

      {/* Reset active filters */}
      {hasActiveFilters && onResetFilters && (
        <button
          onClick={onResetFilters}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--primary)] hover:underline cursor-pointer"
        >
          <RotateCcw className="h-3 w-3" />
          <span>Filter zurücksetzen</span>
        </button>
      )}

      {/* Active Category Drill-Down (subcategories with live counts) */}
      {activeCategoryObj && activeSubcategories.length > 0 && (
        <div className="space-y-2 rounded-2xl border border-[var(--border)] bg-white p-4 shadow-2xs">
          <div className="flex items-center justify-between border-b border-[var(--border)] pb-2.5 mb-2">
            <h3 className="font-display text-xs font-bold uppercase tracking-wider text-[var(--foreground)]">
              {activeCategoryObj.label || activeCategoryObj.name}
            </h3>
          </div>

          <button
            onClick={() => onSelectSubcategory && onSelectSubcategory('')}
            className={`flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-xs font-semibold text-left transition-colors cursor-pointer ${
              !activeSubcategory
                ? 'bg-[var(--primary)] text-[var(--on-primary,white)] shadow-2xs'
                : 'text-[var(--foreground)] hover:bg-[var(--surface-container-low,#f8fafc)]'
            }`}
          >
            <span>Alle Artikel</span>
            <span className={`text-xs ml-1 shrink-0 ${!activeSubcategory ? 'text-white/80' : 'text-[var(--secondary)]'}`}>
              {activeCategoryObj.productCount || activeCategoryObj.product_count || ''}
            </span>
          </button>

          {activeSubcategories.map((sub) => {
            const isSubSelected = activeSubcategory === sub.slug;
            return (
              <button
                key={sub.slug}
                onClick={() => onSelectSubcategory && onSelectSubcategory(sub.slug)}
                className={`flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-xs text-left transition-colors cursor-pointer ${
                  isSubSelected
                    ? 'bg-[var(--primary)]/15 text-[var(--primary)] font-bold'
                    : 'text-[var(--secondary)] hover:bg-[var(--surface-container-low,#f8fafc)] hover:text-[var(--foreground)]'
                }`}
              >
                <span className="truncate">{sub.label || sub.name}</span>
                <span className="text-xs ml-1 shrink-0 text-[var(--secondary)]">
                  {sub.count ?? ''}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {aslTree.length > 0 && (
        <section aria-label="ASL-Kategorien" className="space-y-1 rounded-2xl border border-[var(--border)] bg-white p-4 shadow-2xs">
          <h3 className="mb-2 border-b border-[var(--border)] pb-2 font-display text-xs font-bold uppercase tracking-wider">Kategorien</h3>
          {activeAslPath.length > 0 && (
            <div aria-label="Aktiver ASL-Pfad" className="mb-2 flex flex-wrap items-center gap-1 text-xs">
              {activeAslPath.map((node, index) => (
                <span key={node.key} data-asl-active-path="true" className={index === activeAslPath.length - 1 ? 'font-bold text-[var(--primary)]' : 'font-semibold text-[var(--primary)]'}>
                  {index > 0 && <span aria-hidden="true" className="mr-1 text-[var(--secondary)]">/</span>}{node.name}
                </span>
              ))}
            </div>
          )}
          <button type="button" onClick={() => onSelectAslCategory?.('')} className="mb-1 text-xs font-semibold text-[var(--primary)]">Alle Artikel</button>
          <AslTreeList nodes={visibleAslTree} activeKey={activeAslCategory} activePathKeys={activePathKeys} onSelect={onSelectAslCategory} />
        </section>
      )}

      {/* Manufacturer Checklist */}
      {manufacturers && manufacturers.length > 0 && (
        <div className="space-y-2 rounded-2xl border border-[var(--border)] bg-white p-4 shadow-2xs">
          <div className="flex items-center justify-between border-b border-[var(--border)] pb-2 mb-2">
            <h3 className="font-display text-xs font-bold uppercase tracking-wider text-[var(--foreground)]">
              {`Hersteller (${manufacturers.length})`}
            </h3>
          </div>
          <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
            {manufacturers.map((mf) => {
              const isSelected = selectedManufacturer?.toLowerCase() === mf.toLowerCase();
              return (
                <button
                  key={mf}
                  onClick={() => onSelectManufacturer && onSelectManufacturer(isSelected ? '' : mf)}
                  className={`flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-xs text-left transition-colors cursor-pointer ${
                  isSelected
                    ? 'bg-[var(--primary)]/15 text-[var(--primary)] font-bold'
                    : 'text-[var(--secondary)] hover:bg-[var(--surface-container-low,#f8fafc)] hover:text-[var(--foreground)]'
                }`}
              >
                <span className="truncate">{mf}</span>
                {isSelected && <span className="text-xs font-bold text-[var(--primary)]">✓</span>}
              </button>
            );
          })}
        </div>
      </div>
      )}

    </aside>
  );
}
